import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { CompanyMembership } from '../companies/types';
import type { User } from './types';
import type { Membership } from './membershipTypes';

interface MembershipState {
  memberships: Membership[];
  activeMembershipId: string | null;
  switching: boolean;
  setMemberships: (memberships: Membership[]) => void;
  setActiveMembership: (id: string | null) => void;
  setSwitching: (v: boolean) => void;
  clearMemberships: () => void;
}

export const useMembershipStore = create<MembershipState>()(
  persist(
    (set) => ({
      memberships: [],
      activeMembershipId: null,
      switching: false,
      setMemberships: (memberships) => set({ memberships }),
      setActiveMembership: (activeMembershipId) => set({ activeMembershipId }),
      setSwitching: (switching) => set({ switching }),
      clearMemberships: () => set({ memberships: [], activeMembershipId: null, switching: false }),
    }),
    {
      name: 'fieldsnap-memberships',
      partialize: (state) => ({
        memberships: state.memberships,
        activeMembershipId: state.activeMembershipId,
      }),
    },
  ),
);

function toStoredMembership(membership: CompanyMembership): Membership {
  return {
    membership_id: membership.id,
    company_id: membership.company_id,
    company_name: membership.company_name?.trim() || membership.company_id,
    role: membership.company_role,
  };
}

export function syncMembershipStoreFromUser(user: User | null) {
  const store = useMembershipStore.getState();

  if (!user) {
    store.clearMemberships();
    return;
  }

  const memberships = (user.memberships ?? [])
    .filter((membership) => membership.is_active ?? true)
    .map(toStoredMembership);

  const activeMembershipId =
    memberships.find((membership) => membership.membership_id === user.company_membership_id)?.membership_id ??
    memberships[0]?.membership_id ??
    null;

  store.setMemberships(memberships);
  store.setActiveMembership(activeMembershipId);
}

export function selectActiveMembership(state: MembershipState): Membership | null {
  if (state.memberships.length === 0) return null;
  if (!state.activeMembershipId) return state.memberships[0];
  return (
    state.memberships.find((membership) => membership.membership_id === state.activeMembershipId) ??
    state.memberships[0]
  );
}
